import catalogData from "../../generated/runware/catalog.json";

export type MediaModelType = "audio" | "image" | "video";
export type CatalogModelType = MediaModelType | "other";
export type SupportStatus = "supported" | "unsupported" | "unknown";

export type RunwareCapabilityFieldEvidence = {
	excerpt?: string;
	sourceType: "family-doc" | "model-markdown" | "model-schema" | "model-search" | "provider-doc";
	sourceUrl: string;
};

export type RunwareCapabilityValue<T> = {
	confidence: number;
	evidence: RunwareCapabilityFieldEvidence[];
	required?: boolean;
	status: SupportStatus;
	unsupportedBecauseUnknown?: boolean;
	value: T | null;
};

export type RunwareModelCapabilitySpec = {
	constraints: {
		heightStep: RunwareCapabilityValue<number>;
		maxHeight: RunwareCapabilityValue<number>;
		maxInputResolution: RunwareCapabilityValue<Record<string, unknown>>;
		maxReferenceImages: RunwareCapabilityValue<number>;
		maxWidth: RunwareCapabilityValue<number>;
		minHeight: RunwareCapabilityValue<number>;
		minWidth: RunwareCapabilityValue<number>;
		supportedAspectRatios: RunwareCapabilityValue<string[]>;
		supportedDimensions: RunwareCapabilityValue<
			Array<{ height: number; label?: string; width: number }>
		>;
		supportedDurations: RunwareCapabilityValue<{
			defaultSeconds?: number;
			maxSeconds: number;
			minSeconds: number;
		}>;
		supportedOutputFormats: RunwareCapabilityValue<string[]>;
		supports4k: RunwareCapabilityValue<boolean>;
		widthStep: RunwareCapabilityValue<number>;
	};
	economics: {
		costModel: RunwareCapabilityValue<Record<string, unknown>>;
		pricing: RunwareCapabilityValue<Record<string, unknown>>;
	};
	execution: {
		defaultParams: RunwareCapabilityValue<Record<string, unknown>>;
		deliveryModes: RunwareCapabilityValue<string[]>;
		outputTypes: RunwareCapabilityValue<string[]>;
		requiredParams: RunwareCapabilityValue<string[]>;
		taskType: RunwareCapabilityValue<string>;
	};
	exposure: {
		enabled: boolean;
		isDefault: boolean;
	};
	identity: {
		agentframerId: string;
		displayName: string;
		family: string;
		lastSyncedAt: string;
		modality: CatalogModelType;
		provider: string;
		runwareAirId: string;
		sourceUrls: string[];
		status: string;
		version: string;
	};
	inputs: Record<string, RunwareCapabilityValue<Record<string, unknown> | string | number>>;
	summary: string;
	workflows: Record<string, RunwareCapabilityValue<boolean>>;
};

type RunwareCatalogData = {
	generatedAt: string;
	models: RunwareModelCapabilitySpec[];
	schemaVersion: number;
	sourcePriority: string[];
};

const catalog = catalogData as RunwareCatalogData;

export function getRunwareCatalog() {
	return catalog;
}

export function listCapabilitySpecs(type?: MediaModelType) {
	return catalog.models.filter((model) => (type ? model.identity.modality === type : true));
}

export function listExposedCapabilitySpecs(type?: MediaModelType) {
	return listCapabilitySpecs(type).filter((model) => model.exposure.enabled);
}

export function listReviewableCapabilitySpecs(type?: CatalogModelType) {
	return [...catalog.models]
		.filter((model) => !model.exposure.enabled)
		.filter((model) => (type ? model.identity.modality === type : true))
		.sort((left, right) => {
			if (left.identity.modality !== right.identity.modality) {
				return left.identity.modality.localeCompare(right.identity.modality);
			}
			return left.identity.displayName.localeCompare(right.identity.displayName);
		});
}

export function getModelCapabilitySpec(agentframerId?: string | null, type?: MediaModelType) {
	const models = listExposedCapabilitySpecs(type);
	if (!agentframerId) {
		return models.find((model) => model.exposure.isDefault) ?? models[0] ?? null;
	}
	return models.find((model) => model.identity.agentframerId === agentframerId) ?? null;
}

function blockedMessage(label: string, spec: RunwareCapabilityValue<unknown>) {
	if (spec.status === "unknown") {
		return `${label} is blocked for this model because AgentFramer has not verified support from official Runware docs yet.`;
	}
	return `${label} is not supported by this model.`;
}

function assertSupported(label: string, spec: RunwareCapabilityValue<unknown>) {
	if (spec.status !== "supported") {
		throw new Error(blockedMessage(label, spec));
	}
}

function asRecord(value: unknown) {
	return typeof value === "object" && value ? (value as Record<string, unknown>) : null;
}

function getDefaultNumber(spec: RunwareModelCapabilitySpec, key: string) {
	const params = asRecord(spec.execution.defaultParams.value);
	const value = params?.[key];
	return typeof value === "number" ? value : null;
}

export function getDefaultModelSpecOrThrow(type: MediaModelType) {
	const spec = getModelCapabilitySpec(undefined, type);
	if (!spec) {
		throw new Error(`No ${type} models are currently exposed.`);
	}
	return spec;
}

export function getCapabilitySpecOrThrow(agentframerId: string | null | undefined, type: MediaModelType) {
	const spec = getModelCapabilitySpec(agentframerId, type);
	if (!spec) {
		throw new Error(`Unsupported ${type} model.`);
	}
	return spec;
}

export function validateToolInputAgainstModelSpec(
	tool: "generate_audio" | "generate_image" | "generate_video",
	modelSpec: RunwareModelCapabilitySpec,
	input: Record<string, unknown>,
) {
	if (tool === "generate_image") {
		assertSupported("Text-to-image generation", modelSpec.workflows.supportsTextToImage);
		const width = typeof input.width === "number" ? input.width : getDefaultNumber(modelSpec, "width");
		const height =
			typeof input.height === "number" ? input.height : getDefaultNumber(modelSpec, "height");
		if (!width || !height) {
			throw new Error(`${modelSpec.identity.displayName} does not have verified default dimensions.`);
		}

		const supportedDimensions = modelSpec.constraints.supportedDimensions;
		if (supportedDimensions.status === "supported" && supportedDimensions.value) {
			const match = supportedDimensions.value.find(
				(dimension) => dimension.width === width && dimension.height === height,
			);
			if (!match) {
				const supported = supportedDimensions.value
					.map((dimension) => `${dimension.width}x${dimension.height}`)
					.join(", ");
				throw new Error(
					`${modelSpec.identity.displayName} currently supports these image sizes: ${supported}.`,
				);
			}
		} else {
			assertSupported("Supported image dimensions", supportedDimensions);
		}
	}

	if (tool === "generate_video") {
		assertSupported("Text-to-video generation", modelSpec.workflows.supportsTextToVideo);
		const aspectRatio =
			typeof input.aspectRatio === "string"
				? input.aspectRatio
				: (asRecord(modelSpec.execution.defaultParams.value)?.aspectRatio as string | undefined);
		const durationSeconds =
			typeof input.durationSeconds === "number"
				? input.durationSeconds
				: getDefaultNumber(modelSpec, "durationSeconds");

		assertSupported("Supported video aspect ratios", modelSpec.constraints.supportedAspectRatios);
		assertSupported("Supported video durations", modelSpec.constraints.supportedDurations);

		const aspectRatios = modelSpec.constraints.supportedAspectRatios.value ?? [];
		if (!aspectRatio || !aspectRatios.includes(aspectRatio)) {
			throw new Error(
				`${modelSpec.identity.displayName} currently supports these aspect ratios: ${aspectRatios.join(", ")}.`,
			);
		}

		const durations = modelSpec.constraints.supportedDurations.value;
		if (!durationSeconds || !durations) {
			throw new Error(`${modelSpec.identity.displayName} does not have verified duration limits.`);
		}

		if (
			durationSeconds < durations.minSeconds ||
			durationSeconds > durations.maxSeconds
		) {
			throw new Error(
				`${modelSpec.identity.displayName} currently supports durations between ${durations.minSeconds} and ${durations.maxSeconds} seconds.`,
			);
		}
	}

	if (tool === "generate_audio") {
		assertSupported("Text-to-audio generation", modelSpec.workflows.supportsTextToAudio);
		assertSupported("Supported audio durations", modelSpec.constraints.supportedDurations);
		const durationSeconds =
			typeof input.durationSeconds === "number"
				? input.durationSeconds
				: getDefaultNumber(modelSpec, "durationSeconds");
		const durations = modelSpec.constraints.supportedDurations.value;
		if (!durationSeconds || !durations) {
			throw new Error(`${modelSpec.identity.displayName} does not have verified duration limits.`);
		}
		if (
			durationSeconds < durations.minSeconds ||
			durationSeconds > durations.maxSeconds
		) {
			throw new Error(
				`${modelSpec.identity.displayName} currently supports durations between ${durations.minSeconds} and ${durations.maxSeconds} seconds.`,
			);
		}
	}
}

export function listModelsFromCatalog(type?: MediaModelType) {
	const modalityOrder: Record<CatalogModelType, number> = {
		audio: 2,
		image: 0,
		other: 3,
		video: 1,
	};

	return [...listExposedCapabilitySpecs(type)]
		.sort((left, right) => {
			const modalityDelta =
				modalityOrder[left.identity.modality] - modalityOrder[right.identity.modality];
			if (modalityDelta !== 0) return modalityDelta;
			if (left.exposure.isDefault !== right.exposure.isDefault) {
				return left.exposure.isDefault ? -1 : 1;
			}
			return left.identity.agentframerId.localeCompare(right.identity.agentframerId);
		})
		.map((model) => {
		const defaultParams = asRecord(model.execution.defaultParams.value) ?? {};
		return {
			defaultAspectRatio:
				typeof defaultParams.aspectRatio === "string" ? defaultParams.aspectRatio : undefined,
			defaultDurationSeconds:
				typeof defaultParams.durationSeconds === "number"
					? defaultParams.durationSeconds
					: undefined,
			defaultHeight:
				typeof defaultParams.height === "number" ? defaultParams.height : undefined,
			defaultWidth:
				typeof defaultParams.width === "number" ? defaultParams.width : undefined,
			description: model.summary,
			id: model.identity.agentframerId,
			isDefault: model.exposure.isDefault,
			label: model.identity.displayName,
			pricing: model.economics.pricing.value,
			provider: model.identity.provider,
			providerModelId: model.identity.runwareAirId,
			supportedAspectRatios: model.constraints.supportedAspectRatios.value ?? undefined,
			supportedDimensions: model.constraints.supportedDimensions.value ?? undefined,
			supportedDurations: model.constraints.supportedDurations.value ?? undefined,
			type: model.identity.modality,
		};
	});
}
